import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Check, CheckCheck, Calendar, PhoneIncoming, AlertCircle, BellRing } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { usePushNotifications } from '../hooks/usePushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  appointment_scheduled: Calendar,
  auto_callback: PhoneIncoming,
  default: AlertCircle,
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function NotificationBell() {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);

  const { supported: pushSupported, subscribed: pushSubscribed, subscribe: pushSubscribe, loading: pushLoading } = usePushNotifications();

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    intervalRef.current = setInterval(fetchNotifications, 20000);
    return () => clearInterval(intervalRef.current);
  }, [fetchNotifications]);

  const markAllRead = async () => {
    if (!token || unread === 0) return;
    setLoading(true);
    try {
      await axios.post(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* silent */ }
    setLoading(false);
  };

  const markOneRead = async (notif) => {
    if (!token || notif.read) return;
    try {
      await axios.post(`${API}/notifications/${notif.created_at}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(prev =>
        prev.map(n => n.created_at === notif.created_at ? { ...n, read: true } : n)
      );
      setUnread(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Notifications"
        >
          <Bell size={20} />
          {unread > 0 && (
            <span
              data-testid="notification-unread-badge"
              className="absolute -top-0.5 -right-0.5 h-5 min-w-[20px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 leading-none animate-in zoom-in duration-200"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 p-0 bg-background border-border shadow-xl shadow-black/40"
        data-testid="notification-dropdown"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
          <div className="flex items-center gap-2">
            {pushSupported && !pushSubscribed && (
              <button
                onClick={pushSubscribe}
                disabled={pushLoading}
                data-testid="enable-push-btn"
                className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors disabled:opacity-50"
                title="Enable push notifications"
              >
                <BellRing size={13} />
                Enable
              </button>
            )}
            {pushSubscribed && (
              <span className="flex items-center gap-1 text-[10px] text-emerald-500" data-testid="push-enabled-badge">
                <BellRing size={11} />
                Push on
              </span>
            )}
            {unread > 0 && (
              <button
                onClick={markAllRead}
                disabled={loading}
                data-testid="mark-all-read-btn"
                className="flex items-center gap-1 text-xs text-emerald-400 hover:text-primary transition-colors disabled:opacity-50"
              >
                <CheckCheck size={14} />
                Mark all read
              </button>
            )}
          </div>
        </div>
        <ScrollArea className="max-h-80">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground" data-testid="notification-empty">
              <Bell size={28} className="mb-2 opacity-40" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800/60">
              {notifications.map((notif, i) => {
                const Icon = ICON_MAP[notif.type] || ICON_MAP.default;
                return (
                  <button
                    key={notif.created_at || i}
                    onClick={() => markOneRead(notif)}
                    data-testid={`notification-item-${i}`}
                    className={`w-full text-left px-4 py-3 flex gap-3 transition-colors hover:bg-muted ${
                      notif.read ? 'opacity-60' : ''
                    }`}
                  >
                    <div className={`shrink-0 mt-0.5 p-1.5 rounded-lg ${
                      notif.read ? 'bg-muted text-muted-foreground' : 'bg-emerald-500/15 text-emerald-400'
                    }`}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-snug ${notif.read ? 'text-muted-foreground' : 'text-foreground'}`}>
                        {notif.title || notif.message}
                      </p>
                      {notif.title && notif.message && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notif.message}</p>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-1">{timeAgo(notif.created_at)}</p>
                    </div>
                    {!notif.read && (
                      <div className="shrink-0 mt-2">
                        <span className="block h-2 w-2 rounded-full bg-emerald-400" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
